import {Locale} from '@aboutyou/config';
import {PageTypes} from '@aboutyou/router';
import {DeepMaybe, DeepWriteable} from '@aboutyou/utils';
import {config as backendAbTestsConfig} from '../backend-abtest-overrides';
import {config} from '../defaults';
import {MergedFeatureToggles} from './mergeFeatureFlagsConfigs';

type RawAvailableFeatureToggles = DeepWriteable<typeof config>;
export type AvailableFeatureNames = keyof RawAvailableFeatureToggles;

type RawAvailableBackendABTests = DeepWriteable<typeof backendAbTestsConfig>;
export type AvailableBackendABTestNames = keyof RawAvailableBackendABTests;

export type AvailableFeatureToggles = {
  [Key in AvailableFeatureNames]: RawAvailableFeatureToggles[Key];
};

export type FlagValueString =
  | `${string}-c`
  | `${string}-${number}`
  | `${string}_${number}`;

/**
 * Note: for backward compatibility, `c`, `control`, `1` and `string_number` are kept. However, should be removed
 * once the feature flags that use these are removed
 */
export interface Experiment {
  name: string;
  mapping:
    | Record<FlagValueString, string>
    | Record<'control', string>
    | Record<'c', string>
    | Record<'1', string>;
}

type FeatureFlagValue = boolean | FlagValueString | 'control' | 'c' | '1';

type IsoDateStringWithTimeZone =
  `${Year}-${Month}-${Day}T${Hours}:${Minutes}:${Seconds}${
    | '+'
    | '-'}${Hours}:00`;

type Month = `${number}${number}`;
type Day = `${number}${number}`;
type Year = `${number}${number}`;
type Hours = `${number}${number}`;
type Minutes = `${number}${number}`;
type Seconds = `${number}${number}`;

interface SimpleFeatureFlag {
  defaultValue: FeatureFlagValue;
  timeFrame?: {
    start: {
      time: IsoDateStringWithTimeZone;
      value: FeatureFlagValue;
    };
    end?: {
      time: IsoDateStringWithTimeZone;
      value: FeatureFlagValue;
    };
  };
  enableForLocales?: Readonly<Array<keyof typeof Locale>>;
}

export type PageTypesForABTestCaching = {
  /**
   * `pageTypes` property defines on which pages the feature flag has an effect.
   * - use `global` if the feature flag affects a feature on the App level (eg: header)
   * - use empty array [] if the feature is not server rendered. Example: an AB Test on ADP recommendations (not server rendered). This is because if the page is not server rendered then it doesn't affect cached html.
   */
  pageTypes: Readonly<(PageTypes | 'global')[]>;
};

export type FeatureFlagWithExperiment = SimpleFeatureFlag & {
  experiment: Experiment;
} & PageTypesForABTestCaching;

export type FeatureFlagWithExperiments = SimpleFeatureFlag & {
  experiment: Readonly<Experiment[]>;
} & PageTypesForABTestCaching;

export type FeatureToggleConfig =
  | (SimpleFeatureFlag & {experiment?: never})
  | FeatureFlagWithExperiment
  | FeatureFlagWithExperiments;

export type FeatureFlagOverrides = Record<
  AvailableFeatureNames,
  FeatureFlagValue
>;

export type FeatureToggleOverrides = Record<
  AvailableFeatureNames,
  FeatureToggleConfig
>;

export interface FeatureFlagSetup {
  mergedConfig: MergedFeatureToggles;
}

type InferExperimentType<T> = T extends {
  experiment: infer O extends Record<string, unknown>;
}
  ? {experiment: Array<O>}
  : T;

type ObjectLike<T> = {[index: FlagValueString]: T};

export type InferVariant<E, N, D> = E extends {
  mapping: ObjectLike<infer M>;
  name: N;
}
  ? M
  : D extends string
  ? D
  : never;

export type ExtractExperiment<T> = DeepMaybe<
  InferExperimentType<T>,
  ['experiment', number]
>;

export function isFeatureFlagWithExperiment(
  config: FeatureToggleConfig,
): config is FeatureFlagWithExperiment {
  return 'experiment' in config && !Array.isArray(config.experiment);
}

export function isFeatureFlagWithExperiments(
  config: FeatureToggleConfig,
): config is FeatureFlagWithExperiments {
  return 'experiment' in config && Array.isArray(config.experiment);
}

export function isExperiment(experiment: any): experiment is Experiment {
  return 'mapping' in experiment;
}
